import i18next from "i18next";
import moment from "moment";
import "moment/locale/af";
import "moment/locale/ar";
import "moment/locale/az";
import "moment/locale/bg";
import "moment/locale/bn";
import "moment/locale/bs";
import "moment/locale/cs";
import "moment/locale/cy";
import "moment/locale/da";
import "moment/locale/de";
import "moment/locale/el";
import "moment/locale/es";
import "moment/locale/et";
import "moment/locale/eu";
import "moment/locale/fa";
import "moment/locale/fi";
import "moment/locale/fr";
import "moment/locale/ga";
import "moment/locale/gl";
import "moment/locale/gu";
import "moment/locale/he";
import "moment/locale/hi";
import "moment/locale/hr";
import "moment/locale/hu";
import "moment/locale/id";
import "moment/locale/is";
import "moment/locale/it";
import "moment/locale/ja";
import "moment/locale/ka";
import "moment/locale/kk";
import "moment/locale/km";
import "moment/locale/kn";
import "moment/locale/ko";
import "moment/locale/ky";
import "moment/locale/lo";
import "moment/locale/lt";
import "moment/locale/lv";
import "moment/locale/mk";
import "moment/locale/ml";
import "moment/locale/mn";
import "moment/locale/mr";
import "moment/locale/ms";
import "moment/locale/mt";
import "moment/locale/my";
import "moment/locale/ne";
import "moment/locale/nl";
import "moment/locale/pl";
import "moment/locale/pt";
import "moment/locale/pt-br";
import "moment/locale/ro";
import "moment/locale/ru";
import "moment/locale/sd";
import "moment/locale/si";
import "moment/locale/sk";
import "moment/locale/sl";
import "moment/locale/sq";
import "moment/locale/sr";
import "moment/locale/sv";
import "moment/locale/ta";
import "moment/locale/te";
import "moment/locale/th";
import "moment/locale/tk";
import "moment/locale/tl-ph";
import "moment/locale/tr";
import "moment/locale/uk";
import "moment/locale/ur";
import "moment/locale/uz";
import "moment/locale/vi";
import "moment/locale/zh-cn";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { OverLayParameter } from "../../App";
import * as Configurations from "../../configurations";
import { useTracking } from "../../tools/analytics";
import { getItinerary, putItinerary } from "../../tools/ciblerAPI";
import { RequirementsProps } from "../../tools/context";
import { useWindowSize } from "../../tools/hooks";
import { useTranslation } from "../../tools/i18n";
import { getSanityClient } from "../../tools/sanity";
import { makeAnAlgoliaSearch } from "../../tools/searchAPI";
import {
  getCookies,
  getOrCreateCiblerId,
  getQueryString,
  replaceQueryString,
} from "../../tools/tools";
import Page404 from "../404";
import ButtonV2 from "../ButtonV2";
import { ChatAgent } from "../ChatAgent";
import { Picto } from "../Picto";
import { ResponsiveImage } from "../ResponsiveImage";
import { Months } from "../TravelPlaner/PeriodsOrDates";
import { TravelPlannerConfigType } from "../TravelPlaner/types";
import TravelPlannerErrorComponent from "../TravelPlanerError";
import { TravelPlannerSanityProps } from "../TravelPlanerHub";
import { Beta } from "./Beta";
import { Profile } from "../../tools/mapbox/apiTypes/direction";
import { DirectionContext, DirectionProvider } from "./DirectionContext";
import { FeedbackPopin } from "./FeedbackPopin";
import Footer from "./Footer";
import { Header } from "./Header";
import IframeSpotModal from "./Modal/IframeSpotModal";
import SpotDetailModal from "./Modal/SpotDetailModal";
import { ProgressionBar } from "./ProgressionBar";
import Restaurants from "./Restaurants";
import { Review } from "./Review";
import SitesExplorator, { SitesExploratorSite } from "./SitesExplorator";
import { TravelCustomMap } from "./TravelCustomMap";
import { ChangeActivityDirection, TravelDays } from "./TravelDays";
import { TravelDaysNight } from "./TravelDaysNight";
import {
  TravelPlanerResultsContext,
  TravelPlanerResultsProvider,
} from "./TravelPlanerResultsContext";
import { TravelPlannerResultsConfigType } from "./types";

if (typeof window !== "undefined") {
  require("leaflet/dist/leaflet.css");
}

interface TripPoint {
  type: "site" | "activity";
  city: string;
  latitude: number;
  description: string;
  description_en: string;
  description_es: string;
  description_de: string;
  description_it: string;
  description_nl: string;
  timeEstimate: string;
  matching: string;
  urlimg: string;
  name: string;
  store_name: string;
  time: string;
  formatted_address?: string;
  modalities?: string;
  free?: string;
  possible_fee?: string;
  tca_categ: string;
  id: string | number;
  category: string;
  assigned_disabilities?: string;
  hebergement_accessible_france?: boolean;
  quality?: string;
  longitude: number;
  site?: string;
  tarif?: string;
  price_range?: string;
  start_date?: string;
  end_date?: string;
  tourinsoft_id?: number;
  adr?: string;
  brand?: string;
  interests?: string[];
  services?: string[];
  situations?: string[];
  state?: string;
}

export interface TravelPlanerResultsProps {
  requirements: RequirementsProps;
  navigation: any;
  headerButtons: any;
  componentStates: {
    journey: any;
    restaurants: any;
    articles: any;
    lodgings: any;
    sites: any;
    travelPlanner: TravelPlannerSanityProps;
  };
  setOverlay: (p: OverLayParameter) => void;
  feedbackUrl?: string;
  agentId: string;
  agentKey: string;
  config: TravelPlannerResultsConfigType;
  hideAgentKeyCss: boolean;
  siteConfiguration: any;
}

const cleanLocation = (
  location: string | undefined,
  requirements: RequirementsProps,
): string | undefined => {
  if (!location) return location;

  switch (requirements.partner) {
    case "france":
      return location.replace(/, France$/, "");
    case "martinique":
      return location.replace(/et ses alentours$/, "");
    default:
      return location;
  }
};

const Renderer = (props: TravelPlanerResultsProps) => {
  const { requirements, agentKey, config, hideAgentKeyCss, componentStates } =
    props;
  const { restaurants, lodgings, travelPlanner } = componentStates;
  const { travelForceLogin } = requirements.config;
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [dayOfTheWeek, setDayOfTheWeek] = useState<number>(1);
  const [review, setReview] = useState<"up" | "down">();
  const [showReview, setShowReview] = useState<boolean>(false);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const { t, i18n, language } = useTranslation();
  const { trackEvent } = useTracking();
  const {
    journey,
    trip,
    setTrip,
    day,
    setDay,
    showAllSites,
    setShowAllSites,
    showMobileMap,
    setShowMobileMap,
    formattedData,
    selectedActivities,
    setSelectedActivities,
    sites,
    setSites,
    setInputs,
    spotDetail,
    closeSpotDetail,
    spotIframe,
    closeSpotIframe,
    siteConfiguration,
  } = useContext(TravelPlanerResultsContext);
  const { travelData: results } = journey;
  const inputsOfTravel = journey.inputs.inputs;
  const lodging = results?.inputs?.lodging;
  const lodgingPoint = lodging
    ? lodging.address?.address
      ? lodging.address
      : lodging.hotel
    : null;

  const isHuttopia = requirements.partner === "huttopia";

  useEffect(() => {
    // Use the agentKey to have different configurations for the same partner
    if (agentKey?.length) {
      // Add the configuration for the agent
      requirements.config =
        Configurations[`${requirements.partner}_${agentKey}`] ||
        requirements.config;

      // Add custom CSS
      if (!hideAgentKeyCss) {
        const head = document.getElementsByTagName("head")[0];
        const oldStyle = document.getElementById(
          `customCSS_${requirements.config.key}`,
        );
        const partnerDefaultStyle = document.getElementById("customCSS");
        if (partnerDefaultStyle) {
          partnerDefaultStyle.remove();
        }
        if (!oldStyle) {
          const link = document.createElement("link");
          link.id = `customCSS_${requirements.config.key}}`;
          link.rel = "stylesheet";
          link.type = "text/css";
          link.href = `//${window.location.host}/custom/${requirements.config.key}.css`;
          link.media = "all";
          head.appendChild(link);
        }
      }

      if (!language.includes(agentKey)) {
        const newLanguage = `${language}_${agentKey}`;
        i18n
          .changeLanguage(newLanguage, (err, t) => {
            if (err) return console.error("something went wrong loading", err);
          })
          .catch((e) => console.error("Error changing language", e));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentKey]);


  useEffect(() => {
    const journeyTrip = journey?.travelData?.trip || {};
    if (!journeyTrip || day) return;
    const keys = Object.keys(journeyTrip);
    setDay(keys.length ? keys[0] : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey]);

  useEffect(() => {
    const dayOfTheWeek = moment(results?.inputs?.StartDate).day();
    setDayOfTheWeek(dayOfTheWeek);
  }, [results]);

  const handleSetDays = (d: string) => {
    const cookies = getCookies();
    if (cookies.loginToken || !travelForceLogin) {
      setDay(d);
      return;
    }
    props.setOverlay({ key: "loginPopup", context: { queries: { day: d } } });
  };

  const handleReview = async (value: any) => {
    if (review) return;

    trackEvent(`review${value}_click`);
    setReview(value);
    const { endpoint, config } = requirements;
    const id = requirements.parameters[0];
    const headers = new Headers();
    await fetch(
      `${endpoint}/api/poi/journey/${id}/feedback?positive=${
        value === "up"
      }&customer=${config.customerId}&ciblerId=${getOrCreateCiblerId()}&info`,
      {
        headers,
        method: "POST",
      },
    );
    if (value === "down") {
      setShowFeedback(true);
    }
  };

  useEffect(() => {
    const cookies = getCookies();
    let newDay = null;
    if (cookies.loginToken || !travelForceLogin) {
      const d = getQueryString("day");
      if (d) {
        newDay = d;
        replaceQueryString("day", null);
      }
      const review = getQueryString("review");
      if (review) {
        handleReview(review);
        replaceQueryString("review", null);
      }
    }
    if (getQueryString("auth")) {
      setShowReview(true);
    }

    if (results && results.trip && !newDay) {
      const keys = Object.keys(results.trip);
      newDay = keys.length ? keys[0] : "";
    }
    setDay(newDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!results) return <Page404 {...props} />;
  if (!results.trip || Object.keys(results.trip).length === 0) {
    // TEMP
    if (isHuttopia) {
      return (
        <div className="no-spots">
          <p>{t("travelPlaner.NoSpot.message")}</p>
          <ButtonV2
            className="button"
            onClick={() => (window.location.href = "/")}
          >
            {t("travelPlaner.NoSpot.buttonLabel")}
          </ButtonV2>
        </div>
      );
    }
    return <TravelPlannerErrorComponent />;
  }
  const { inputs } = results || {};
  const showStandardRestaurants =
    config.showRestaurants && (restaurants?.length || 0) > 0;
  const showFullScreenRestaurants =
    config.fullScreen &&
    config.showRestaurants &&
    (restaurants?.length || 0) > 0;
  const showFullScreenLodgings =
    config.fullScreen &&
    config.showLodgings &&
    (lodgings?.length || 0) > 0 &&
    !lodgingPoint;

  const handleChangeActivity = (
    direction: ChangeActivityDirection,
    key: string,
  ) => {
    const currentData = formattedData[day].find(
      (d: { key: string; value: any }) => d.key === key,
    );
    const copy = { ...selectedActivities };

    if (direction === "prev")
      copy[day][key] =
        copy[day][key] === 0
          ? currentData.value.length - 1
          : copy[day][key] - 1;
    else
      copy[day][key] =
        copy[day][key] === currentData.value.length - 1
          ? 0
          : copy[day][key] + 1;

    setSelectedActivities(copy);
  };

  if (!results) return <Page404 {...props} />;

  const id = requirements.parameters[0];

  const getFormattedDates = (): string => {
    if (results.inputs?.startMonth)
      return Object.keys(Months)?.includes(results.inputs?.startMonth)
        ? t(`travelPlaner.PeriodsOrDates.months.${results.inputs?.startMonth}`)
        : results.inputs?.startMonth;
    if (!results.inputs?.StartDate && !results.inputs?.startDate) return "";
    const rawLang = language || i18n?.language || i18next.language || "fr";
    const momentLocale = rawLang.split(/[-_]/)[0];
    const start = new Date(
      results.inputs?.StartDate || results.inputs?.startDate,
    );
    const nbDays = results.inputs?.DayNumber || results.inputs?.tripLength || 1;
    if (nbDays === 1) return moment(start).locale(momentLocale).format("D MMMM YY");
    const end = new Date(
      results.inputs?.StartDate || results.inputs?.startDate,
    );
    const checkoutOffset = isHuttopia ? nbDays : nbDays - 1;
    end.setDate(end.getDate() + checkoutOffset);
    return `${moment(start).locale(momentLocale).format("D MMMM YYYY")} > ${moment(end).locale(momentLocale).format("D MMMM YYYY")}`;
  };

  const handleMapAction = (action: string): void => {
    if (action === "sites") {
      setShowAllSites(true);
      setShowMobileMap(false);
    }
  };

  const handleDeleteTravelPoint = (index: number, dayIndex?: number): void => {
    const finalDayIndex =
      typeof dayIndex === "number"
        ? dayIndex
        : Object.keys(trip).findIndex((d) => d === day);
    setSites(
      sites.map((s) =>
        s.pointIndex === index && s.dayIndex === finalDayIndex
          ? { ...s, dayIndex: undefined, pointIndex: undefined }
          : s,
      ),
    );
    const days = Object.keys(trip);
    const isNightTrip = Object.values(trip).some((d: any) =>
      Object.keys(d || {}).some((k) => k.startsWith("night")),
    );

    const newTrip: Record<string, any> = {};
    days.forEach((d, dIndex) => {
      const points = Object.keys(trip[d]);
      const newPoints = {};
      points.forEach((p, pointIndex) => {
        if (index !== pointIndex || finalDayIndex !== dIndex)
          newPoints[p] = trip[d][p];
      });
      const dayHasContent = Object.keys(newPoints).length > 0;
      if (!isNightTrip || dayHasContent) {
        newTrip[d] = newPoints;
      }
    });

    setTrip(newTrip);
    const newTripLength = Object.keys(newTrip).length;
    if (isNightTrip) {
      setInputs?.((prev: any) => ({ ...prev, tripLength: newTripLength }));
    }
    const newDayKeys = Object.keys(newTrip);
    if (day && !newDayKeys.includes(day)) {
      setDay(newDayKeys[0] || "");
    }
    putItinerary(
      id,
      isNightTrip
        ? {
            ...results,
            inputs: { ...results.inputs, tripLength: newTripLength },
            trip: newTrip,
          }
        : { ...results, trip: newTrip },
      requirements,
    );
  };

  const convertSiteToTripPoint = (s: SitesExploratorSite): TripPoint => ({
    type: s.type || "site",
    id: s.id,
    latitude: s.lat,
    longitude: s.lng,
    name: s.store_name,
    description: s.post_content,
    description_en: s.post_content_en,
    description_es: s.post_content_es,
    description_de: s.post_content_de,
    description_it: s.post_content_it,
    description_nl: s.post_content_nl,
    formatted_address: s.type === "site" ? s.formatted_address : undefined,
    modalities: s.type === "site" ? s.modalities : undefined,
    free: s.type === "site" ? s.free : undefined,
    possible_fee: s.type === "site" ? s.possible_fee : undefined,
    tca_categ: s.type === "site" ? s.tca_categ : "",
    store_name: s.store_name,
    urlimg: s.urlimg,
    city: "",
    timeEstimate: "",
    category: s.category ?? s.categ_str,
    matching: "",
    time: "",
    assigned_disabilities: s.assigned_disabilities?.join(", "),
    hebergement_accessible_france: s.hebergement_accessible_france,
    quality: s.quality,
    site: s.type === "activity" ? s.site : undefined,
    tarif: s.type === "activity" ? s.tarif : undefined,
    price_range: s.type === "activity" ? s.price_range : undefined,
    start_date: s.type === "activity" ? s.start_date : undefined,
    end_date: s.type === "activity" ? s.end_date : undefined,
    tourinsoft_id: s.type === "site" ? s.tourinsoft_id : undefined,
    adr: s.type === "site" ? s.adr : undefined,
    brand: s.type === "site" ? s.brand : undefined,
    interests: s.type === "site" ? s.interests : undefined,
    services: s.type === "site" ? s.services : undefined,
    situations: s.type === "site" ? s.situations : undefined,
    state: s.type === "site" ? s.state : undefined,
  });

  const handleChangeTravelPoint = (
    dayIndex: number,
    point: SitesExploratorSite,
  ): void => {
    const days = Object.keys(trip);
    const isNightJourney = Object.values(formattedData || {}).some((d: any) =>
      d?.some((i: any) => i.key?.startsWith("night")),
    );

    if (isNightJourney && dayIndex >= days.length) {
      if (typeof point.dayIndex === "number") return;
      const maxNum = days.reduce((m, k) => {
        const n = parseInt(k.replace(/\D/g, ""), 10);
        return !isNaN(n) && n > m ? n : m;
      }, 0);
      const newDayNum = Math.max(days.length, maxNum) + 1;
      const newDayKey = `JOUR ${newDayNum}`;
      const newTrip = {
        ...trip,
        [newDayKey]: {
          [`night_${point.id}`]: [convertSiteToTripPoint(point)],
        },
      };

      setSites(
        sites.map((s) =>
          s.id === point.id
            ? { ...point, dayIndex: days.length, pointIndex: 0 }
            : s,
        ),
      );

      const newTripLength = Object.keys(newTrip).length;
      setTrip(newTrip);
      setInputs?.((prev: any) => ({ ...prev, tripLength: newTripLength }));
      putItinerary(
        id,
        {
          ...results,
          inputs: {
            ...results.inputs,
            tripLength: newTripLength,
          },
          trip: newTrip,
        },
        requirements,
      );
      return;
    }

    if (dayIndex === -1 || dayIndex === point.dayIndex) return;

    if (isNightJourney) {
      const targetDay = days[dayIndex];
      const nightKey = Object.keys(trip[targetDay] || {}).find((k) =>
        k.startsWith("night"),
      );
      const newTrip: Record<string, any> = {};
      days.forEach((d) => {
        if (d === targetDay && nightKey) {
          newTrip[d] = {
            ...trip[d],
            [nightKey]: [convertSiteToTripPoint(point)],
          };
        } else {
          newTrip[d] = trip[d];
        }
      });

      setSites(
        sites.map((s) =>
          s.id === point.id ? { ...point, dayIndex, pointIndex: 0 } : s,
        ),
      );
      setTrip(newTrip);
      putItinerary(id, { ...results, trip: newTrip }, requirements);
      return;
    }

    const targetDay = days[dayIndex];
    const targetDayPoints = Object.keys(trip[targetDay]);
    const pointIndex = targetDayPoints.length;

    setSites(
      sites.map((s) =>
        s.id === point.id ? { ...point, dayIndex, pointIndex } : s,
      ),
    );

    const newTrip = {};
    const newTripPointKey = `added_${point.id}`;

    days.forEach((d, dIndex) => {
      if (dIndex === dayIndex) {
        newTrip[d] = {
          ...trip[d],
          [newTripPointKey]: [convertSiteToTripPoint(point)],
        };
      } else {
        newTrip[d] = {};
        Object.keys(trip[d]).forEach((tp) => {
          if (tp !== newTripPointKey) {
            newTrip[d][tp] = trip[d][tp];
          }
        });
      }
    });

    setTrip(newTrip);
    putItinerary(id, { ...results, trip: newTrip }, requirements);
  };

  const interests = {
    ...inputsOfTravel.interests,
    ...inputsOfTravel.newInterests,
  };
  let selectedInterests = Object.keys(interests).filter(
    (i) => interests[i].note === 3,
  );

  if (selectedInterests.length < 1) {
    selectedInterests = Object.keys(results?.inputs?.interests).filter(
      (i) => interests[i] === 3,
    );
  }

  const agentContext = simplifyTrip(
    Object.keys(formattedData)?.map((key: string) =>
      formattedData[key]?.map((d: any, index: number) => ({
        key: index,
        value: d.value?.map((v: any) => ({
          ...v,
          time: index,
        })),
      })),
    ),
    selectedActivities,
  );

  const travelInputs = {
    search: results?.inputs?.search || inputsOfTravel?.search || "",
    searchMap: results?.inputs?.searchMap || inputsOfTravel?.searchMap || "",
    startDate: moment(
      results?.inputs?.StartDate ||
        results?.inputs?.startDate ||
        inputsOfTravel?.startDate,
    ).isValid()
      ? moment(
          results?.inputs?.StartDate ||
            results?.inputs?.startDate ||
            inputsOfTravel?.startDate,
        ).format("DD/MM")
      : moment().format("DD/MM"),
    tripLength:
      results?.inputs?.DayNumber ||
      results?.inputs?.tripLength ||
      inputsOfTravel?.dayNumber ||
      1,
  };

  const getPostalCodes = () => {
    if (results.inputs?.postalCodes) {
      return results.inputs.postalCodes;
    }
    if (results.inputs?.searchMap?.postalCodes) {
      return results.inputs.searchMap.postalCodes;
    }
    if (results.inputs?.coordinates) {
      return results.inputs.coordinates.map((coord) => coord.postalCode);
    }
    return results.inputs?.postalCode || "";
  };

  const postalCodes = getPostalCodes();

  const isNight = formattedData?.[day]?.some((d: any) =>
    d.key?.startsWith("night"),
  );

  return (
    <div
      className={`travelPlanerResultsNewUI ${
        config.showHeader ? "withHeader" : ""
      } ${config.fullScreen ? "fullScreen" : ""}`}
    >
      {inputs.cityKey ? (
        <ResponsiveImage
          className="headerImage"
          requirements={requirements}
          src={`https://cdn.cibler.io/static/tca/v2/travelPlaner/${inputs.cityKey}.jpg`}
          alt={inputs.cityKey}
          retry={3}
        />
      ) : (
        <div className="headerPlaceholder" />
      )}
      {config.showHeader ? (
        <Header
          componentStates={componentStates}
          setAgentOverlay={props.setOverlay}
          setDays={handleSetDays}
        />
      ) : null}
      {config.showProgression ? (
        <ProgressionBar
          length={travelPlanner.content?.[0]?.config?.steps?.length || 0}
        />
      ) : null}
      {config.fullScreen && showMobileMap && !isMobile ? (
        <div className="backButton" onClick={() => setShowMobileMap(false)}>
          <Picto iconKey="arrow-fr" />
          <span>
            {t(`travelPlaner.Travel.sites.back${showAllSites ? "Sites" : ""}`)}
          </span>
        </div>
      ) : undefined}
      <div
        className={`travelPlanerResultsContent ${
          config.fullScreen
            ? showMobileMap
              ? "showMobileMap"
              : "showMobileDays"
            : ""
        }`}
      >
        <div className="content-wrapper">
          {!config.fullScreen ? (
            <div className="content">
              <a
                className="backButton backButtonHome"
                href={travelPlanner?.path?.current || "/guide"}
              >
                <Picto iconKey={"arrow-left"} />
              </a>
              {config.isBetaHidden ? null : (
                <Beta
                  isInline={config.inlineBeta}
                  title={t("travelPlaner.Travel.beta.title") || ""}
                  content={t("travelPlaner.Travel.beta.content")}
                />
              )}
              <h2 className="title">{t("travelPlaner.Travel.title")}</h2>
              {config.showTripLength ? (
                <div className="tripLength">
                  {t("travelPlaner.Travel.tripLength", {
                    count: inputs.tripLength || 1,
                  })}
                </div>
              ) : null}
              {config.showDescription ? (
                <h4 className="description">
                  {t("travelPlaner.Travel.description")}
                </h4>
              ) : null}
            </div>
          ) : showAllSites ? undefined : (
            <div className="leftHeader">
              <div className="leftHeaderContent">
                <div className="travelPlaceTitle">
                  {config.isBetaHidden ? null : (
                    <Beta
                      title={t("travelPlaner.Travel.beta.title") || ""}
                      content={t("travelPlaner.Travel.beta.content")}
                    />
                  )}
                  {cleanLocation(
                    results.inputs?.search || results.inputs?.searchMap,
                    requirements,
                  )}
                </div>
                <div className="travelDatesSubtitle">{getFormattedDates()}</div>
                <div className="travelPlanerSummary">
                  {!isHuttopia &&
                    (inputsOfTravel?.search || inputsOfTravel.searchMap) && (
                      <div>
                        <span>{`${t("travelPlaner.Search.title")} :`}</span>
                        <span>
                          {" "}
                          {cleanLocation(
                            inputsOfTravel?.search || inputsOfTravel.searchMap,
                            requirements,
                          )}
                        </span>
                      </div>
                    )}
                  {inputsOfTravel.pace && (
                    <div>
                      <span>{t("travelPlaner.Pace.title")} :</span>
                      <span>
                        {t(`travelPlaner.Pace.${inputsOfTravel.pace}`)}
                      </span>
                    </div>
                  )}
                  {inputsOfTravel.times && (
                    <div>
                      <span>{t("travelPlaner.Times.title")} :</span>
                      <span>
                        {t("travelPlaner.Times.title")} : $
                        {t(`travelPlaner.Times.${inputsOfTravel.times}`)}
                      </span>
                    </div>
                  )}
                  {inputsOfTravel.budget && (
                    <div>
                      <span>{t("travelPlaner.Budget.title")} :</span>
                      <span>
                        {t(`travelPlaner.Budget.${inputsOfTravel.budget}`)}
                      </span>
                    </div>
                  )}
                  {inputsOfTravel.lodging &&
                    (inputsOfTravel.lodging.address ||
                      inputsOfTravel.lodging.hotel) && (
                      <div>
                        <span>{t("travelPlaner.Lodging.title")} :</span>
                        <span>
                          {inputsOfTravel.lodging.address.name ||
                            inputsOfTravel.lodging.hotel.name}
                        </span>
                      </div>
                    )}
                  {inputsOfTravel.dayNumber && (
                    <div>
                      <span>
                        {t("travelPlaner.DayNumber.title")} : $
                        {inputsOfTravel.dayNumber}
                      </span>
                    </div>
                  )}
                  {inputsOfTravel.composition && (
                    <div>
                      <span>{t("travelPlaner.Composition.title")} :</span>
                      <span>
                        {t(
                          `travelPlaner.Composition.${inputsOfTravel.composition}`,
                        )}
                      </span>
                    </div>
                  )}
                  {!isHuttopia && inputsOfTravel.startDate && (
                    <div>
                      <span>{t("travelPlaner.StartDate.title")} :</span>
                      <span>{getFormattedDates()}</span>
                    </div>
                  )}
                  {selectedInterests.length > 0 && (
                    <div>
                      <span>{t("travelPlaner.Interests.title")} :</span>
                      <span>
                        {selectedInterests
                          .map((i) => t(`travelPlaner.Interests.keys.${i}`))
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  {results?.inputs?.placeOfDeparture?.label ? (
                    <div>
                      <span>
                        {t("travelPlaner.PlaceOfDeparture.departure")} :
                      </span>
                      <span>{results?.inputs?.placeOfDeparture?.label}</span>
                    </div>
                  ) : null}
                  {results?.inputs?.placeOfArrival?.label ? (
                    <div>
                      <span>
                        {t("travelPlaner.PlaceOfDeparture.arrival")} :
                      </span>
                      <span>{results?.inputs?.placeOfArrival?.label}</span>
                    </div>
                  ) : null}
                  {results?.inputs?.travelerProfile?.equipment ? (
                    <div>
                      <span>
                        {t("travelPlaner.TravelerProfile.equipment")} :
                      </span>
                      <span>
                        {t(
                          `travelPlaner.TravelerProfile.equipmentList.${results?.inputs?.travelerProfile?.equipment}`,
                        )}
                      </span>
                    </div>
                  ) : null}
                  {results?.inputs?.travelerProfile?.adultCount ? (
                    <div>
                      <span>
                        {t("travelPlaner.TravelerProfile.adultLabel")} :
                      </span>
                      <span>
                        {results?.inputs?.travelerProfile?.adultCount}
                      </span>
                    </div>
                  ) : null}
                  {results?.inputs?.travelerProfile?.childrenCount ? (
                    <div>
                      <span>
                        {t("travelPlaner.TravelerProfile.childrenLabel")} :
                      </span>
                      <span>
                        {results?.inputs?.travelerProfile?.childrenCount}
                      </span>
                    </div>
                  ) : null}
                  {results?.inputs?.travelerProfile?.childrenAges.length > 0
                    ? results?.inputs?.travelerProfile?.childrenAges.map(
                        (c, index) => (
                          <div key={`childrenAges_${index}`}>
                            <span>
                              {`${t("travelPlaner.TravelerProfile.chidAge")} ${index + 1}`}{" "}
                              :
                            </span>
                            <span>{c}</span>
                          </div>
                        ),
                      )
                    : null}
                  {results?.inputs?.travelerProfile?.havePets ? (
                    <div>
                      <span>{t("travelPlaner.TravelerProfile.askPets")}:</span>
                      <span>
                        {results?.inputs?.travelerProfile?.havePets
                          ? t(
                              "travelPlaner.TravelerProfile.askPetsResponse.yes",
                            )
                          : t(
                              "travelPlaner.TravelerProfile.askPetsResponse.no",
                            )}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
          {showStandardRestaurants && !config.fullScreen ? (
            <Restaurants
              requirements={requirements}
              restaurants={restaurants}
              dayOfTheWeek={dayOfTheWeek}
            />
          ) : null}
        </div>
        {config.showDays && !config.fullScreen ? (
          <DaysHeader
            showWhereToEat={showStandardRestaurants}
            showWhereToSleep={lodging?.length > 0}
            travelPlanner={travelPlanner}
          />
        ) : null}
        {day ? (
          <>
            {(!isMobile ||
              !config.fullScreen ||
              (isMobile && showMobileMap)) && (
              <TravelCustomMap
                day={day}
                journey={journey}
                config={config}
                requirements={requirements}
                inputs={inputs}
                doAction={handleMapAction}
                travelPlanner={travelPlanner}
              />
            )}
            <BackgroundDirectionFetcher day={day} />

            {showAllSites ? (
              <SitesExplorator
                config={config}
                requirements={requirements}
                onBack={() => setShowAllSites(false)}
                setShowMobileMap={setShowMobileMap}
                onChange={handleChangeTravelPoint}
                onDelete={handleDeleteTravelPoint}
                isNightTrip={isNight}
                days={Object.keys(formattedData).map((dayKey, i) => {
                  if (isNight) {
                    const spotEntry = formattedData[dayKey]?.find((entry: any) =>
                      entry?.key?.startsWith("night"),
                    );
                    const spotName =
                      spotEntry?.value?.[0]?.store_name ||
                      spotEntry?.value?.[0]?.name;
                    if (spotName) return `Spot ${i + 1} - ${spotName}`;
                  }
                  return `${t("travelPlaner.Travel.day")} ${i + 1}`;
                })}
              />
            ) : isNight ? (
              <TravelDaysNight
                dayIndex={Object.keys(formattedData).indexOf(day) + 1}
                requirements={requirements}
                allData={formattedData}
                resultsInOneTab={config?.resultsInOneTab}
              />
            ) : (
              <TravelDays
                config={config}
                isEditable={config.fullScreen}
                requirements={requirements}
                data={formattedData?.[day]}
                day={day}
                selectedActivities={selectedActivities?.[day]}
                handleChangeActivity={handleChangeActivity}
                restaurants={restaurants}
                lodgingPoint={lodgingPoint}
                lodgings={lodgings}
                dayOfTheWeek={dayOfTheWeek}
                dayIndex={Object.keys(formattedData).indexOf(day) + 1}
                cityKey={inputs.cityKey}
                postalCode={postalCodes}
              />
            )}
          </>
        ) : null}
        {config.resultsInOneTab ||
        (config.showDays && config.fullScreen && !showAllSites) ? (
          <DaysHeader
            showWhereToEat={showFullScreenRestaurants}
            showWhereToSleep={showFullScreenLodgings}
            travelPlanner={travelPlanner}
          />
        ) : null}
        {config.fullScreen && !showAllSites ? (
          <Footer
            setShowMobileMap={setShowMobileMap}
            requirements={requirements}
            isEditable={config.fullScreen}
            travelInputs={travelInputs}
            isNight={isNight}
          />
        ) : undefined}
      </div>
      {showReview && !config.fullScreen ? (
        <Review
          requirements={requirements}
          label={t("travelPlaner.Travel.review")}
          thanksText={t("travelPlaner.Travel.reviewThanks")}
          setShowFeedback={setShowFeedback}
        />
      ) : null}
      {config.hideChatAgent || config.chatAgentInHeader ? undefined : (
        <ChatAgent
          key={
            JSON.stringify(formattedData) + JSON.stringify(selectedActivities)
          }
          requirements={requirements}
          agentId={props.agentId}
          context={agentContext}
          setOverlay={props.setOverlay}
        />
      )}
      {showFeedback && props.feedbackUrl && (
        <FeedbackPopin
          callback={() => setShowFeedback(false)}
          url={props.feedbackUrl.replace("{{id}}", id)}
        />
      )}
      {config.fullScreen ? <MobileBottomMenu /> : null}
      <SpotDetailModal
        isOpen={!!spotDetail}
        spot={spotDetail}
        onClose={() => closeSpotDetail?.()}
        requirements={requirements}
        setOverlay={props.setOverlay}
        siteConfiguration={siteConfiguration}
      />
      <IframeSpotModal
        isOpen={!!spotIframe}
        url={spotIframe?.url}
        title={spotIframe?.title}
        onClose={() => closeSpotIframe?.()}
      />
    </div>
  );
};

type BottomMenuPage = "itinerary" | "map" | "allSites";

const ITINERARY_FALLBACKS: Record<string, string> = {
  fr: "Itinéraire",
  en: "Itinerary",
  de: "Reiseroute",
};

const MobileBottomMenu = () => {
  const { t, language } = useTranslation();
  const {
    showAllSites,
    setShowAllSites,
    showMobileMap,
    setShowMobileMap,
    config,
  } = useContext(TravelPlanerResultsContext);

  const baseLang = (language || "fr").split(/[-_]/)[0];

  const itineraryLabel = (() => {
    const translated = t("travelPlaner.Travel.itinerary");
    if (translated && translated !== "travelPlaner.Travel.itinerary") {
      return translated;
    }
    return ITINERARY_FALLBACKS[baseLang] ?? ITINERARY_FALLBACKS.fr;
  })();

  const sitesLink = config?.mapLinks?.find((l: any) => l?.action === "sites");
  const sitesLabel = sitesLink?.key
    ? t(`travelPlaner.Travel.${sitesLink.key}`)
    : t("travelPlaner.Travel.sites.title");
  const sitesIcon = sitesLink?.picto || "search-fr";

  const goTo = (target: BottomMenuPage) => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    if (target === "itinerary") {
      setShowAllSites(false);
      setShowMobileMap(false);
    } else if (target === "map") {
      setShowAllSites(false);
      setShowMobileMap(true);
    } else {
      setShowAllSites(true);
      setShowMobileMap(false);
    }
  };

  const current: BottomMenuPage = showAllSites
    ? "allSites"
    : showMobileMap
      ? "map"
      : "itinerary";

  const labels: Record<BottomMenuPage, string> = {
    itinerary: itineraryLabel,
    map: t("travelPlaner.Travel.showMap"),
    allSites: sitesLabel,
  };

  const icons: Record<BottomMenuPage, string> = {
    itinerary: "itinerary-fr",
    map: "guide-icon",
    allSites: sitesIcon,
  };

  const pages: BottomMenuPage[] = ["itinerary", "map", "allSites"];

  return (
    <nav className="mobileBottomMenu" aria-label="Navigation">
      {pages.map((page) => {
        const isActive = page === current;
        return (
          <button
            key={page}
            type="button"
            className={`mobileBottomMenuButton mobileBottomMenuButton--${page}${isActive ? " active" : ""}`}
            aria-current={isActive ? "page" : undefined}
            onClick={() => goTo(page)}
          >
            <span className="mobileBottomMenuIcon">
              <Picto iconKey={icons[page] as any} />
            </span>
            <span className="mobileBottomMenuLabel">{labels[page]}</span>
          </button>
        );
      })}
    </nav>
  );
};

const BackgroundDirectionFetcher = ({ day }: { day: string }) => {
  const { formattedData, selectedActivities } = useContext(
    TravelPlanerResultsContext,
  );
  const { updateDirection, currentProfile } = useContext(DirectionContext);

  const points = useMemo(() => {
    const isNightTrip = Object.values(formattedData)?.some((d: any) =>
      d?.some((i: any) => i.key?.startsWith("night")),
    );
    const dayData = formattedData[day];
    const data = isNightTrip
      ? Object.values(formattedData)?.map(
          (d: any) =>
            d?.find((i: any) => i.key?.startsWith("night"))?.value?.[0],
        )
      : dayData?.map(
          (d: { key: string; value: any }) =>
            d.value[selectedActivities?.[day]?.[d.key]],
        );
    return (
      data?.filter((item: any) => item?.latitude && item?.longitude) || []
    );
  }, [formattedData, selectedActivities, day]);

  useEffect(() => {
    if (!updateDirection || points.length < 2) return;
    const profile = currentProfile ?? Profile.DRIVING;
    updateDirection(points, profile);
  }, [updateDirection, points, currentProfile]);

  return null;
};

export const simplifyTrip = (
  trip: any,
  selectedActivities: any = {},
): string => {
  // Create a deep copy of the trip object
  let tripForAgent = JSON.parse(JSON.stringify(trip));

  for (let day in tripForAgent) {
    for (let time in tripForAgent[day]) {
      const activityIndex = selectedActivities?.[day]?.[time] || 0;
      let firstChoice = tripForAgent[day][time]?.value?.[activityIndex];
      if (!firstChoice) continue;

      // Remove unwanted properties
      delete firstChoice.urlimg;
      delete firstChoice.gifts;
      delete firstChoice.matching;
      delete firstChoice.tourinsoft_id;
      delete firstChoice.longitude;
      delete firstChoice.latitude;
      delete firstChoice.tca_categ;

      // Keep only the first choice for each time
      tripForAgent[day][time] = firstChoice;
    }
  }

  return JSON.stringify(tripForAgent);
};

interface DaysProps {
  hideAI?: boolean;
  showWhereToEat?: boolean;
  showWhereToSleep?: boolean;
  travelPlanner?: any;
}

export const DaysHeader = ({
  hideAI = false,
  showWhereToEat = true,
  showWhereToSleep = true,
  travelPlanner,
}: DaysProps) => {
  const { t, language } = useTranslation();
  const {
    agentId,
    setOverlay,
    config,
    inputs,
    requirements,
    formattedData,
    selectedActivities,
    day,
    setDay,
    selectedPart,
    handleGoTo,
  } = useContext(TravelPlanerResultsContext);
  const daysContainerRef = React.useRef<HTMLDivElement>(null);

  const fakeDays = Array.from({ length: inputs?.tripLength }, (_, i) => ({
    key: Object.keys(formattedData)[i],
    index: i + 1,
    label: config.fullScreen
      ? `${t("travelPlaner.Travel.day")} ${i + 1}`
      : moment(inputs.startDate).add(i, "day").format("DD/MM"),
  }));

  useEffect(() => {
    const container = daysContainerRef.current;
    if (!container) return;
    const selected = container.querySelector<HTMLButtonElement>(
      "button.selected",
    );
    if (!selected) return;
    const containerRect = container.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    const delta =
      selectedRect.left -
      containerRect.left +
      selectedRect.width / 2 -
      containerRect.width / 2;
    container.scrollTo({
      left: container.scrollLeft + delta,
      behavior: "smooth",
    });
  }, [day]);

  const agentContext = simplifyTrip(
    Object.keys(formattedData)?.map((key: string) =>
      formattedData[key]?.map((d: any, index: number) => ({
        key: index,
        value: d.value?.map((v: any) => ({
          ...v,
          time: index,
        })),
      })),
    ),
    selectedActivities,
  );

  const redictionUrl = (clientKey: string) => {
    switch (clientKey) {
      case "martinique":
        return travelPlanner?.path?.current || "/v2";
      case "huttopia":
        return travelPlanner?.path?.current || "/";
      default:
        return travelPlanner?.path?.current || "/guide";
    }
  };
  return (
    <div className="days" id="days">
      <a
        className="backButton backButtonHome"
        href={redictionUrl(requirements?.config?.key)}
      >
        <Picto iconKey={"arrow-left"} />
      </a>
      {config.hideChatAgent ||
      !config.chatAgentInHeader ||
      !config.fullScreen ||
      hideAI ? undefined : (
        <ChatAgent
          key={
            JSON.stringify(formattedData) + JSON.stringify(selectedActivities)
          }
          requirements={requirements}
          agentId={agentId}
          context={agentContext}
          setOverlay={setOverlay}
          withLabel
          hideHighlight
        />
      )}
      <div className="days-container" ref={daysContainerRef}>
        {fakeDays.map((d) => {
          if (requirements?.config?.key === "huttopia" && !d.key) return null;
          return (
            <button
              key={`daylabel_${d.index}`}
              onClick={() => {
                if (!!!d.key) return;
                setDay(d.key);
                if (config?.resultsInOneTab) {
                  const element =
                    document.getElementById(`travelPoint_${d.index - 1}`) ||
                    document.getElementById(
                      `${t("travelPlaner.Travel.day")}${d.index}`,
                    );
                  if (element) {
                    if (typeof window !== "undefined") {
                      (window as any).__suppressActiveSpotUntil =
                        Date.now() + 800;
                    }
                    const offset = -150;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition =
                      elementPosition + window.pageYOffset + offset;

                    window.scrollTo({
                      top: offsetPosition,
                      behavior: "smooth",
                    });
                  }
                } else {
                  handleGoTo("dayPoints");
                }
              }}
              className={d.key ? (d.key === day ? "selected" : "") : "skeleton"}
            >
              {config.fullScreen
                ? `${t("travelPlaner.Travel.day")} ${d.index}`
                : moment(inputs.startDate)
                    .add(d.index, "day")
                    .format(language === "fr" ? "DD/MM" : "MM/DD")}
            </button>
          );
        })}
        {showWhereToEat ? (
          <button
            key="daylabel_eat"
            className={selectedPart === "restaurants" ? "selected" : ""}
            onClick={() => handleGoTo("restaurants")}
          >
            {t("travelPlaner.Travel.whereToEat")}
          </button>
        ) : undefined}
        {showWhereToSleep ? (
          <button
            key="daylabel_sleep"
            className={selectedPart === "lodgings" ? "selected" : ""}
            onClick={() => handleGoTo("lodgings")}
          >
            {t("travelPlaner.Travel.whereToSleep")}
          </button>
        ) : undefined}
      </div>
    </div>
  );
};

const preloader = async (
  data: any,
  requirements: RequirementsProps,
  storybookData?: {
    config: TravelPlannerResultsConfigType;
    travelPlanner: {
      path: {
        current: string;
      };
      content: [
        {
          config: TravelPlannerConfigType;
        },
      ];
    };
  },
) => {
  if (!requirements.parameters.length) return null;
  let id = requirements.parameters[0];

  const partner = requirements.partner;

  let journey = await getItinerary(id, requirements, i18next.language);

  let { config, travelPlanner } = storybookData || {};

  if (!storybookData) {
    const client = getSanityClient(requirements);
    const query = `*[_type == "page" && (path.current=="${requirements.path}") && !(_id in path("drafts.**"))][0]`;
    const results = await client.fetch(query);
    config = results?.content?.[0]?.config || {};
    const travelPlannerRef = config?.travelPlannerRef?._ref;

    travelPlanner = travelPlannerRef
      ? await client.fetch(
          `*[_id == "${travelPlannerRef}" && !(_id in path("drafts.**"))][0]`,
        )
      : undefined;
  }

  const { travelData } = journey;
  const { lat: tpLat, lon: tpLon, radius } = travelData?.inputs || {};

  const sites =
    config.mapLinks?.some((l) => l?.action === "sites") || config.fullScreen
      ? await makeAnAlgoliaSearch(
          {
            ...data,
            radius: ["huttopia", "ccvba"].includes(partner)
              ? 5000000000
              : radius || 10000,
            hitsPerPage: ["huttopia", "ccvba"].includes(partner)
              ? 1000
              : undefined,
          },
          requirements,
          [],
          ["huttopia", "ccvba"].includes(partner)
            ? ""
            : "NOT categslug:ou-sejourner AND NOT categslug:a-table AND NOT categ_str:restaurant AND NOT categslug:commerce",
          tpLat,
          tpLon,
        )
      : [];

  return {
    journey,
    sites,
    travelPlanner,
  };
};

const RendererWithProviders = (props: TravelPlanerResultsProps) => {
  return (
    <DirectionProvider>
      <TravelPlanerResultsProvider
        agentId={props.agentId}
        setOverlay={props.setOverlay}
        initialJourney={props.componentStates.journey}
        config={props.config}
        requirements={props.requirements}
        initialSites={props.componentStates.sites}
        siteConfiguration={props.siteConfiguration}
        travelPlanner={props.componentStates.travelPlanner}
      >
        <Renderer {...props} />
      </TravelPlanerResultsProvider>
    </DirectionProvider>
  );
};

const TravelPlanerResults = { Renderer: RendererWithProviders, preloader };

export { preloader, Renderer as RendererWithProviders };
export default TravelPlanerResults;
